import { useEffect, useRef, useState } from "react";
import "../../../styles/error-fallback.css";

/*
  Where the report is sent. This uses Web3Forms, the same service as the contact form.
  Create a SEPARATE access key for error reports (so they land in their own inbox) and put it in .env:
    REACT_APP_WEB3FORMS_ERROR_KEY=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
  It falls back to the contact form key if the error key is not set.
*/
const REPORT_URL = "https://api.web3forms.com/submit";
const ACCESS_KEY =
  process.env.REACT_APP_WEB3FORMS_ERROR_KEY ||
  process.env.REACT_APP_WEB3FORMS_KEY ||
  "";

// Errors thrown when a lazy-loaded page can't be downloaded (usually after a new deploy)
const isChunkError = (error) =>
  /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module/i.test(
    `${error?.name || ""} ${error?.message || ""}`,
  );

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": "true",
};
const Icon = {
  arrow: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  ),
  flag: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="1.8">
      <path d="M5 21V4M5 4h11l-2 4 2 4H5" />
    </svg>
  ),
  check: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2.2">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12.5l3 3 5-6" />
    </svg>
  ),
  alert: (
    <svg viewBox="0 0 24 24" {...stroke} strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5M12 16v.1" />
    </svg>
  ),
};

export default function ErrorFallback({
  error,
  errorId = "ERR-UNKNOWN",
  reset,
  componentStack,
}) {
  const [status, setStatus] = useState("idle"); // idle | sending | sent | failed
  const headingRef = useRef(null);
  const formRef = useRef(null);
  const chunkError = isChunkError(error);

  useEffect(() => {
    const previous = document.title;
    document.title = "Something went wrong - Global Tech9 LLC";
    headingRef.current?.focus(); // announce the problem to screen readers
    return () => {
      document.title = previous;
    };
  }, []);

  const reload = () => window.location.reload();

  // Submits the hidden form. All values are read at click time, so the component stack is included once React has reported it.
  const sendReport = async (e) => {
    e.preventDefault();
    if (status === "sending" || status === "sent") return;
    setStatus("sending");

    const fields = new FormData(formRef.current);
    const payload = Object.fromEntries(fields.entries());

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const res = await fetch(REPORT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      setStatus(res.ok && data.success !== false ? "sent" : "failed");
    } catch (err) {
      setStatus("failed");
    } finally {
      clearTimeout(timer);
    }
  };

  return (
    <main className="gt9-er">
      <section className="gt9-er__hero">
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <i className="gt9-dot" />
        <div className="gt9-container">
          <span className="gt9-eyebrow">Unexpected error</span>

          <div className="gt9-ill" aria-hidden="true">
            <svg viewBox="0 0 120 120">
              <defs>
                <linearGradient id="gt9-er-ring" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stopColor="#f1d6a4" />
                  <stop offset="1" stopColor="#b98b4e" />
                </linearGradient>
              </defs>
              <g className="spin">
                <circle
                  cx="60"
                  cy="60"
                  r="57"
                  fill="none"
                  stroke="#d2a566"
                  strokeOpacity=".5"
                  strokeWidth="1.6"
                  strokeDasharray="2 7"
                />
              </g>
              <circle
                cx="60"
                cy="60"
                r="45"
                fill="none"
                stroke="url(#gt9-er-ring)"
                strokeWidth="9"
              />
              <g className="pulse">
                <path
                  d="M60 33L88 81H32Z"
                  fill="rgba(255,255,255,.08)"
                  stroke="#fff"
                  strokeWidth="5"
                  strokeLinejoin="round"
                />
                <path
                  d="M60 51v14"
                  stroke="#d2a566"
                  strokeWidth="5.5"
                  strokeLinecap="round"
                />
                <circle cx="60" cy="73.5" r="3.4" fill="#d2a566" />
              </g>
            </svg>
          </div>

          <h1 className="gt9-er__title" tabIndex={-1} ref={headingRef}>
            Something went <span className="hl">wrong</span>
          </h1>
          <p className="gt9-er__lead">
            {chunkError
              ? "A new version of the site may be available. Please reload the page to continue."
              : "An unexpected error occurred while loading this page. Please try again. If the problem continues, our team is happy to help."}
          </p>

          <div className="gt9-actions">
            {chunkError ? (
              <button
                type="button"
                className="gt9-btn gt9-btn--gold"
                onClick={reload}
              >
                Reload page {Icon.arrow}
              </button>
            ) : (
              <button
                type="button"
                className="gt9-btn gt9-btn--gold"
                onClick={reset}
              >
                Try again {Icon.arrow}
              </button>
            )}
            {/* plain link on purpose: a full page load always recovers a broken app state */}
            <a
              href="/"
              className="gt9-btn gt9-btn--ghost"
              style={{ boxShadow: "unset" }}
            >
              Back to home
            </a>
          </div>
        </div>
      </section>

      <div className="gt9-er__body">
        <div className="gt9-container">
          <section className="gt9-panel" aria-labelledby="gt9-er-report">
            <div className="gt9-report">
              <div className="gt9-report__main">
                <i className="gt9-report__icon">{Icon.flag}</i>
                <div>
                  <h2 id="gt9-er-report">Still stuck on the same page?</h2>
                  <p>
                    Tell our team about it with one click. We will look into it
                    and fix it as soon as we can.
                  </p>
                </div>
              </div>
              <button
                type="submit"
                form="gt9-er-form"
                className="gt9-btn gt9-btn--gold"
                disabled={status === "sending" || status === "sent"}
              >
                {status === "sending" ? (
                  <>
                    <span className="gt9-spin" aria-hidden="true" /> Sending…
                  </>
                ) : status === "sent" ? (
                  "Report sent"
                ) : (
                  <>Report the issue {Icon.arrow}</>
                )}
              </button>
            </div>

            {/* Hidden form: nothing here is shown to the visitor */}
            <form
              id="gt9-er-form"
              ref={formRef}
              onSubmit={sendReport}
              className="gt9-hidden"
              aria-hidden="true"
            >
              <input type="hidden" name="access_key" value={ACCESS_KEY} />
              <input
                type="hidden"
                name="subject"
                value={`Website error report ${errorId}`}
              />
              <input
                type="hidden"
                name="from_name"
                value="Website error reporter"
              />
              <input type="hidden" name="error_id" value={errorId} />
              <input
                type="hidden"
                name="error_name"
                value={error?.name || ""}
              />
              <input
                type="hidden"
                name="error_message"
                value={error?.message || String(error || "")}
              />
              <input
                type="hidden"
                name="traceback"
                value={(error?.stack || "").slice(0, 4000)}
              />
              <input
                type="hidden"
                name="component_stack"
                value={(componentStack || "").slice(0, 4000)}
              />
              <input
                type="hidden"
                name="page_url"
                value={
                  typeof window !== "undefined" ? window.location.href : ""
                }
              />
              <input
                type="hidden"
                name="user_agent"
                value={
                  typeof navigator !== "undefined" ? navigator.userAgent : ""
                }
              />
              <input
                type="hidden"
                name="reported_at"
                value={new Date().toISOString()}
              />
              <input
                type="checkbox"
                name="botcheck"
                tabIndex={-1}
                autoComplete="off"
                style={{ display: "none" }}
              />
            </form>

            <div aria-live="polite">
              {status === "sent" && (
                <div className="gt9-alert gt9-alert--success" role="status">
                  {Icon.check}
                  <div>
                    <b>Thank you, we have your report.</b>
                    Our team has been notified. Reference {errorId}.
                  </div>
                </div>
              )}
              {status === "failed" && (
                <div className="gt9-alert gt9-alert--error" role="alert">
                  {Icon.alert}
                  <div>
                    <b>We could not send the report.</b>
                    Please check your connection and press the button again.
                  </div>
                </div>
              )}
            </div>

            <p className="gt9-fine">
              The report includes the page address and technical details about
              the error. No personal information is sent.
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}
